import { useEffect, useReducer, useState } from "react";

import { Colors } from "@blueprintjs/core";
import { toast } from "sonner";

import { Box, Flex } from "@/components/layout/flexbox";
import { SmallCaps } from "@/components/pieces/form/SmallCaps";
import { ToggleField } from "@/components/pieces/form/ToggleField";
import { MenuHeader } from "@/components/pieces/menu/MenuHeader";
import {
  LoggedInNavDataQueryDocument,
  NotificationPreferenceType,
  useSaveNotificationPreferencesMutation,
  WorkspaceNavQueryDocument,
} from "@/graphql";
import { WorkspaceNavUser } from "@/types/users";
import { NonNullableArrayItem } from "@/types/utils";

import NotificationSvg from "./NotificationSvg";

type PrefActionType = "SET_PREF" | "SET_TIME";
type NotificationPreference =
  | "generalChatEnabled"
  | "directMessagesEnabled"
  | "meetingMessagesEnabled"
  | "reviewMessagesEnabled"
  | "announcementsEnabled";

interface PrefState {
  generalChatEnabled: boolean;
  directMessagesEnabled: boolean;
  meetingMessagesEnabled: boolean;
  reviewMessagesEnabled: boolean;
  announcementsEnabled: boolean;
}

interface PrefAction {
  type: PrefActionType;
  pref: NotificationPreference;
  value: boolean;
}

function reducer(state: PrefState, action: PrefAction) {
  switch (action.type) {
    case "SET_PREF":
      return {
        ...state,
        [action.pref]: action.value,
      };
    case "SET_TIME":
      return {
        ...state,
        time: action.value,
      };
    default:
      console.log("Unknown action: " + action.type);
      return state;
  }
}

type Preference = NonNullableArrayItem<
  WorkspaceNavUser["notificationPreferences"]
>;

interface MenuNotificationsProps {
  user: WorkspaceNavUser;
  onClose: () => void;
  onBackLink: () => void;
  isOpen: boolean;
}

export function MenuNotifications({
  user,
  onClose,
  onBackLink,
  isOpen,
}: MenuNotificationsProps) {
  const prefs = user.notificationPreferences?.reduce<
    Record<NotificationPreferenceType, Preference>
  >(
    (obj, pref) => {
      obj[pref.type] = pref;
      return obj;
    },
    {} as Record<NotificationPreferenceType, Preference>,
  );

  const emailOn = (pref: NotificationPreferenceType) =>
    !!prefs[pref] && prefs[pref].email;

  const [notifState, dispatch] = useReducer(reducer, {
    generalChatEnabled: emailOn(NotificationPreferenceType.GeneralChat),
    directMessagesEnabled: emailOn(NotificationPreferenceType.DirectMessage),
    meetingMessagesEnabled: emailOn(NotificationPreferenceType.MeetingMessage),
    reviewMessagesEnabled: emailOn(NotificationPreferenceType.ReviewMessage),
    announcementsEnabled: emailOn(NotificationPreferenceType.Announcements),
  });

  const [mutate] = useSaveNotificationPreferencesMutation({
    variables: {
      input: notifState,
    },
    refetchQueries: [
      { query: LoggedInNavDataQueryDocument },
      { query: WorkspaceNavQueryDocument },
    ],
    onError: () => {
      toast.error("An error occurred. Please try again.");
    },
    onCompleted: (response) => {
      const { errors } = response.saveNotificationPreferences ?? {};
      if (errors?.length) {
        toast.error("An error occurred. Please try again.");
      } else {
        toast.success("Saved");
      }
    },
  });

  function submit() {
    mutate();
  }

  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(true);
  }, []);

  useEffect(() => {
    if (ready && isOpen) {
      submit();
    }
  }, [notifState]);

  return (
    <Box width={1}>
      <MenuHeader
        onBackLink={() => {
          onBackLink();
        }}
        onClose={() => {
          onClose();
        }}
        label="Notifications"
      />

      <Box className="text-center" py={3}>
        <Flex justifyContent="center">
          <NotificationSvg />
        </Flex>

        <Box mt={3}>
          <strong>Notification Settings</strong>
        </Box>
      </Box>

      <Box px={24} pb={1} pt={4}>
        <SmallCaps>Real-time Notifications</SmallCaps>
      </Box>
      <Box style={{ backgroundColor: Colors.WHITE }} py={3} px={24}>
        <Flex justifyContent="space-between" alignItems="center" mt={3}>
          <Box>
            <strong>General Chat</strong>
          </Box>
          <ToggleField
            checked={notifState.generalChatEnabled}
            onChange={(enabled) => {
              dispatch({
                type: "SET_PREF",
                pref: "generalChatEnabled",
                value: enabled,
              });
            }}
          />
        </Flex>
        <Box style={{ color: Colors.GRAY3 }} mt={1}>
          Sent real-time via email
        </Box>

        <Flex justifyContent="space-between" alignItems="center" mt={3}>
          <Box>
            <strong>Direct Messages</strong>
          </Box>
          <ToggleField
            checked={notifState.directMessagesEnabled}
            onChange={(enabled) => {
              dispatch({
                type: "SET_PREF",
                pref: "directMessagesEnabled",
                value: enabled,
              });
            }}
          />
        </Flex>
        <Box style={{ color: Colors.GRAY3 }} mt={1}>
          Sent real-time via email
        </Box>

        <Flex justifyContent="space-between" alignItems="center" mt={3}>
          <Box>
            <strong>Meeting Messages</strong>
          </Box>
          <ToggleField
            checked={notifState.meetingMessagesEnabled}
            onChange={(enabled) => {
              dispatch({
                type: "SET_PREF",
                pref: "meetingMessagesEnabled",
                value: enabled,
              });
            }}
          />
        </Flex>
        <Box style={{ color: Colors.GRAY3 }} mt={1}>
          Sent real-time via email
        </Box>

        <Flex justifyContent="space-between" alignItems="center" mt={3}>
          <Box>
            <strong>Review Messages</strong>
          </Box>
          <ToggleField
            checked={notifState.reviewMessagesEnabled}
            onChange={(enabled) => {
              dispatch({
                type: "SET_PREF",
                pref: "reviewMessagesEnabled",
                value: enabled,
              });
            }}
          />
        </Flex>
        <Box style={{ color: Colors.GRAY3 }} mt={1}>
          Sent real-time via email
        </Box>
      </Box>

      <Box px={24} pb={1} pt={4}>
        <SmallCaps>Occassional Emails</SmallCaps>
      </Box>
      <Box style={{ backgroundColor: Colors.WHITE }} py={3} px={24}>
        <Flex justifyContent="space-between" alignItems="center">
          <Box>
            <strong>Announcements</strong>
          </Box>
          <ToggleField
            checked={notifState.announcementsEnabled}
            onChange={(enabled) => {
              dispatch({
                type: "SET_PREF",
                pref: "announcementsEnabled",
                value: enabled,
              });
            }}
          />
        </Flex>
        <Box style={{ color: Colors.GRAY3 }} mt={1}>
          Learn about new features, content, etc.
        </Box>
      </Box>

      <Box pb={5} mb={4} />
    </Box>
  );
}
